import { computed, ref } from 'vue';
import { concat } from 'lodash-es';
import type { PassengerKey, Passenger } from '@aviata/booking-data';
import type { Airflow } from '@aviata/platform-types';

import {
  checkIfPassengerHasRequiredDocumentFields,
  isSamePersistedPassenger,
  localPassengersStorage,
} from '../lib';
import type { PassengerWithDocType } from '../types';

export function usePassengersForm(schema: Airflow.Offer.Schema.Passengers) {
  // ==================== Работа с сохраненными пассажирами ====================
  const savedPassengers = ref<Passenger[]>([]);
  const localPassengers = ref<Passenger[]>([]);

  function getLocalPassengers(): Passenger[] {
    // Two-pass dedup against the raw session-storage list:
    // 1. Drop any local entry that matches a saved passenger by id or by a
    //    non-empty document number — saved passengers always win.
    // 2. Drop duplicates within the local list itself (same id / same
    //    non-empty doc number appearing twice).
    const localList = localPassengersStorage.read();
    const seen: Passenger[] = [];
    for (const pax of localList) {
      const collidesWithSaved = savedPassengers.value.some(savedPax =>
        isSamePersistedPassenger(savedPax, pax),
      );
      if (collidesWithSaved) {
        continue;
      }
      const collidesWithEarlier = seen.some(earlier =>
        isSamePersistedPassenger(earlier, pax),
      );
      if (collidesWithEarlier) {
        continue;
      }
      seen.push(pax);
    }
    return seen;
  }

  const combinedPassengers = computed(() =>
    concat(savedPassengers.value, localPassengers.value),
  );

  const handleSavedPassengerUpdate = (
    idx: number,
    updatedPassenger: Passenger,
  ) => {
    savedPassengers.value[idx] = { ...updatedPassenger };
  };

  const updateLocalPassenger = (idx: number, updated: Passenger) => {
    const localIdx = idx - savedPassengers.value.length;
    localPassengers.value[localIdx] = { ...updated };
    localPassengersStorage.write(
      localPassengers.value as PassengerWithDocType[],
    );
  };

  // ==================== Управление активным пассажиром ====================
  const activePassengerKey = ref<PassengerKey | null>(null);
  const activeSelectedPassenger = ref<Passenger | null>(null);
  const passengerIdxToEdit = ref<number | null>(null);
  const passengerOnCardForForm = ref<Passenger | null>(null);
  const isNewPassenger = ref(false);

  const activePassengerType = computed(() => {
    return activePassengerKey.value?.slice(0, 3) || '';
  });

  const activeSchema = computed(() => {
    return activePassengerKey.value ? schema[activePassengerKey.value] : null;
  });

  return {
    // Работа с сохраненными пассажирами
    savedPassengers,
    localPassengers,
    combinedPassengers,
    getLocalPassengers,
    handleSavedPassengerUpdate,
    updateLocalPassenger,

    // Управление активным пассажиром
    activePassengerKey,
    activeSelectedPassenger,
    passengerIdxToEdit,
    passengerOnCardForForm,
    isNewPassenger,
    activePassengerType,
    activeSchema,

    // Валидация
    checkIfPassengerHasRequiredDocumentFields,
  };
}
